/**
 * Indicateurs Terrain : SHIELD, HazID, TIF, VPC, ARED (onglets shadcn Tabs).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { dashboardApi } from "@/lib/api";
import { useFilters } from "@/contexts/FilterContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SummaryChip from "@/components/tables/SummaryChip";
import DataTable from "@/components/tables/DataTable";
import IdLinkCell from "@/components/tables/IdLinkCell";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGES } from "@/constants/testIds";

const INDICATORS = [
  {
    key: "shield", label: "SHIELD", fetcher: "getShield",
    columns: [
      { key: "site", label: "Site" }, { key: "year", label: "Année" }, { key: "month", label: "Mois" },
      { key: "email", label: "Auteur (HSE)" }, { key: "observed_email", label: "Personne observée" },
      { key: "start_date", label: "Début" }, { key: "end_date", label: "Fin" },
    ],
  },
  {
    key: "hazid", label: "HazID", fetcher: "getHazid",
    columns: [
      { key: "id", label: "Numéro" }, { key: "site", label: "Site" }, { key: "year", label: "Année" }, { key: "month", label: "Mois" },
      { key: "severity", label: "Gravité" }, { key: "status", label: "Statut" }, { key: "email", label: "Déclarant" },
    ],
  },
  {
    key: "tif", label: "TIF", fetcher: "getTif",
    columns: [
      { key: "site", label: "Site" }, { key: "year", label: "Année" }, { key: "month", label: "Mois" },
      { key: "user_name", label: "Utilisateur" }, { key: "status", label: "Statut tournée" }, { key: "taux", label: "Heures terrain" },
    ],
  },
  {
    key: "vpc", label: "VPC", fetcher: "getVpc",
    columns: [
      { key: "id", label: "Numéro" }, { key: "site", label: "Site" }, { key: "year", label: "Année" }, { key: "month", label: "Mois" },
      { key: "name", label: "Personne" }, { key: "type", label: "Type" }, { key: "topic", label: "Sujet" },
    ],
  },
  {
    key: "ared", label: "ARED", fetcher: "getAred",
    columns: [
      { key: "site", label: "Site" }, { key: "equipment", label: "Equipement" }, { key: "operation", label: "Operation" },
      { key: "year", label: "Année" }, { key: "status", label: "Statut" },
    ],
  },
];

const renderCell = (col, row) => {
  if (col.key === "id") {
    return <IdLinkCell id={row.id} href={row.link} testId={`indicateurs-link-${row.id}`} />;
  }
  return undefined;
};

export default function IndicateursPage() {
  const { filters, refreshKey } = useFilters();
  const [searchParams] = useSearchParams();
  const [active, setActive] = useState(() => searchParams.get("tab") || "shield");
  const [dataByKey, setDataByKey] = useState({});

  useEffect(() => {
    const current = INDICATORS.find((i) => i.key === active);
    dashboardApi[current.fetcher](filters).then(({ data }) => {
      setDataByKey((prev) => ({ ...prev, [active]: data }));
    });
  }, [active, filters, refreshKey]);

  const currentData = dataByKey[active];

  return (
    <div data-testid={PAGES.indicateurs} className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">Indicateurs Terrain</h1>
      <Tabs value={active} onValueChange={setActive}>
        <TabsList data-testid="indicateurs-tabs-list">
          {INDICATORS.map((i) => (
            <TabsTrigger key={i.key} value={i.key} data-testid={`indicateurs-tab-${i.key}`}>
              {i.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {INDICATORS.map((i) => (
          <TabsContent key={i.key} value={i.key} className="space-y-4 pt-4">
            {active === i.key && currentData ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <SummaryChip label="Total lignes" value={currentData.summary?.total ?? currentData.items.length} />
                  {currentData.summary?.sum_value !== undefined && (
                    <SummaryChip label="Total valeur" value={currentData.summary.sum_value} tone="blue" />
                  )}
                  {currentData.summary?.average !== undefined && (
                    <SummaryChip label="Moyenne" value={currentData.summary.average} tone="blue" />
                  )}
                </div>
                <DataTable
                  testId={`${i.key}-table`}
                  title={i.label}
                  columns={i.columns}
                  data={currentData.items}
                  renderCell={renderCell}
                />
              </>
            ) : (
              <Skeleton className="h-72 rounded-lg" />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
